import { APP_INITIALIZER, NgModule } from '@angular/core';

import { FailedRequestInterceptor } from '@app/interceptors/failed-request.interceptor';
import { DEFAULT_TIMEOUT, TimeoutRequestInterceptor } from '@app/interceptors/timeout-request.interceptor';
import { UserInfoModule } from '@app/modules/user-info/user-info.module';
import { EffectsModule } from '@ngrx/effects';
import { META_REDUCERS, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CoreModule } from '@app/core/core.module';
import { LocationModule } from '@app/modules/location/location.module';
import { FilterEffects } from '@app/store/filters/effects/filter.effects';
import { RouterEffects } from '@app/store/router/effects/router.effects';
import { environment } from '@environments/environment';
import { AppComponent } from './app.component';
import { metaReducers, REDUCERS_TOKEN, reducersProvider } from './store';
import { AssetsEffects } from './store/asset/effects/assets.effects';
import { SearchFiltersEffects } from '@app/store/filters/effects/search-filters.effects';
import { PermissionsEffects } from '@app/store/permissions/effects/permissions.effects';
import { PlacesEffects } from '@app/store/places/effects/places.effects';
import { PlatformEffects } from '@app/modules/platform/store/effects/platform.effects';
import { AuthHttpInterceptor, AuthModule, AuthService } from '@auth0/auth0-angular';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
// import { ZonarOwnerIdInterceptor } from '@app/modules/auth';
import { ErrorHandlerModule } from '@app/modules/error-handler/error-handler.module';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { DriverEffects } from '@app/store/driver/effects/driver.effects';
import { AutocompleteEffects } from '@app/store/autocomplete/effects/autocomplete.effects';

import { ZonarUICoreModule } from '@app/modules/core';
import { SidenavModule } from '@app/modules/sidenav';
import { I18nService, TranslateService, ZonarUiI18nModule } from '@livemaps/i18n';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Translations } from '@app/core/services/i18n/translations.service';
import { languageFallbackMap } from '@app/core/constants/language-fallback-map';
import { AuthMapService } from './services/auth-map.service';
import { AnonymizedDataResponseProvider } from './interceptors/anonymized-data-response.interceptor';
import { SettingsComponent } from './modules/shared/components/settings/settings.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppConfigService } from './services/app-config/app-config.service';

export function loadMapsTranslations(
  translateService: TranslateService,
  i18nService: I18nService,
  translations: Translations,
  appConfigService: AppConfigService
): () => Promise<any> {
  return () => {
    return firstValueFrom(
      appConfigService.loadConfig().pipe(
        switchMap(() => {
          const userLang = appConfigService.getUserLang();
          return translateService.use(userLang).pipe(
            tap(() => {
              translations.translationsLoadState.next(ResourceLoadState.LOAD_SUCCESSFUL);
            })
          );
        }),
        catchError(err => {
          console.error('Error during translation setup:', err);
          return of(null);
        })
      )
    );
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    LocationModule,
    CoreModule,
    UserInfoModule,
    ErrorHandlerModule,
    SidenavModule.forRoot({ stage: environment?.authEnv || null }),
    ZonarUICoreModule,
    ZonarUiI18nModule,
    FormsModule,
    ReactiveFormsModule,
    StoreModule.forRoot(REDUCERS_TOKEN, { metaReducers }),
    !environment.devTools ? [] : StoreDevtoolsModule.instrument({ logOnly: !environment.devTools }),
    EffectsModule.forRoot([
      AssetsEffects,
      FilterEffects,
      PlatformEffects,
      PermissionsEffects,
      SearchFiltersEffects,
      PlacesEffects,
      RouterEffects,
      DriverEffects,
      AutocompleteEffects
    ]),
    AuthModule.forRoot({
      clientId: environment.auth.clientID,
      domain: environment.auth.domain,
      audience: environment.auth.audience,
      redirect_uri: window.location.origin,
      scope: 'openid profile email',
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
      connection: environment.auth.connection,
      httpInterceptor: {
        allowedList: [
          `${environment.locationApi.url}/*`,
          `${environment.geocoderApiBase.url}/*`,
          `${environment.pathApiBase.url}/*`,
          `${environment.vdofleet.baseApiUrl}/*`
        ]
      }
    }),
    StoreRouterConnectingModule.forRoot()
  ],
  providers: [
    reducersProvider,
    AnonymizedDataResponseProvider,
    { provide: 'region', useValue: environment.region },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: ZonarOwnerIdInterceptor, multi: true },
    { provide: DEFAULT_TIMEOUT, useValue: 120000000 },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FailedRequestInterceptor,
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutRequestInterceptor, multi: true },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: 'applicationId', useValue: environment.auth.applicationId },
    { provide: 'supportedLanguages', useValue: environment.i18n.supportedLanguages },
    { provide: 'defaultLanguage', useValue: environment.i18n.defaultLanguage },
    { provide: 'useBrowserLanguage', useValue: true },
    { provide: 'Window', useValue: window },
    { provide: MAT_DATE_LOCALE, useValue: environment.i18n.defaultLanguage },
    {
      provide: APP_INITIALIZER,
      useFactory: (authMapService: AuthMapService) => () => authMapService.checkAuth(),
      deps: [AuthMapService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadMapsTranslations,
      deps: [TranslateService, I18nService, Translations, AppConfigService],
      multi: true
    }
  ]
})
export class AppModule {}
